import * as React from 'react';
import { Copy, RefreshCw } from 'lucide-react';

import { Button } from '../primitives/button';
import { cn } from '../lib/utils';

/**
 * Shared chrome for every degraded-state surface: the tinted panel the
 * surfaces render inside, and the standard failure footer.
 *
 * `DegradedPanel` is deliberately INTERNAL to the degraded-state family — it
 * is imported by the sibling modules and is not re-exported from the
 * `degraded-state` barrel, because a view that reaches for the raw panel can
 * put any copy it likes inside it, which is exactly the freedom the taxonomy
 * exists to remove.
 */

// ─── Retry + correlation id ───────────────────────────────────────────

export interface RetryWithCorrelationProps {
  onRetry?: () => void;
  correlationId?: string;
  /** Label for the retry control. Default "Retry". */
  retryLabel?: string;
  /** Disable retry (e.g. while rate-limited and the cooldown has not elapsed). */
  retryDisabled?: boolean;
  className?: string;
}

/**
 * The standard failure footer: one retry control plus the support
 * correlation id, copyable in a single click. An operator quotes the token
 * instead of narrating the failure, and support can find the exact request.
 */
export function RetryWithCorrelation({
  onRetry,
  correlationId,
  retryLabel = 'Retry',
  retryDisabled,
  className,
}: RetryWithCorrelationProps) {
  const [copied, setCopied] = React.useState(false);

  const copy = React.useCallback(() => {
    if (!correlationId) return;
    const clipboard = globalThis.navigator?.clipboard;
    // Best effort: a denied clipboard permission must not break the retry
    // affordance next to it, and the id stays selectable as text regardless.
    void Promise.resolve(clipboard?.writeText(correlationId)).catch(() => undefined);
    setCopied(true);
  }, [correlationId]);

  if (!onRetry && !correlationId) return null;

  return (
    <div
      className={cn('mt-3 flex flex-wrap items-center gap-3', className)}
      data-testid="degraded-retry-footer"
    >
      {onRetry && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRetry}
          disabled={retryDisabled}
          data-testid="degraded-retry"
        >
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {retryLabel}
        </Button>
      )}
      {correlationId && (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>Reference</span>
          <code
            className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground"
            data-testid="degraded-correlation-id"
          >
            {correlationId}
          </code>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={copy}
            aria-label={`Copy reference ${correlationId}`}
            data-testid="degraded-correlation-copy"
          >
            <Copy className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <span aria-live="polite" className="sr-only">
            {copied ? 'Reference copied' : ''}
          </span>
        </span>
      )}
    </div>
  );
}

// ─── Shared panel chrome ──────────────────────────────────────────────

export type Tone = 'danger' | 'warning' | 'info' | 'success';

// Body text stays on `text-foreground` in every tone: a tinted panel that
// also recolours its prose fails AA in one of the two themes (the
// `*-foreground` tokens are chosen for SOLID fills, not 10% washes). Tone
// is carried by the border, the wash, and the icon only.
const TONE_CLASS: Record<Tone, string> = {
  danger: 'border-destructive/50 bg-destructive/10 text-foreground',
  warning: 'border-warning/50 bg-warning/10 text-foreground',
  info: 'border-border bg-muted/40 text-foreground',
  success: 'border-success/50 bg-success/10 text-foreground',
};

const TONE_ICON_CLASS: Record<Tone, string> = {
  danger: 'text-destructive',
  warning: 'text-warning',
  info: 'text-muted-foreground',
  success: 'text-success',
};

export interface PanelProps {
  tone: Tone;
  icon: React.ReactNode;
  title: string;
  testId: string;
  /** `alert` interrupts; `status` is polite. Safety-relevant states assert. */
  assertive?: boolean;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function DegradedPanel({
  tone,
  icon,
  title,
  testId,
  assertive,
  children,
  footer,
  className,
}: PanelProps) {
  return (
    <div
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
      data-testid={testId}
      className={cn('rounded-md border p-3 text-sm', TONE_CLASS[tone], className)}
    >
      <div className="flex items-start gap-2">
        <span className={cn('mt-0.5 shrink-0', TONE_ICON_CLASS[tone])} aria-hidden>
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium">{title}</p>
          {children && <div className="mt-1">{children}</div>}
          {footer}
        </div>
      </div>
    </div>
  );
}
